import { InventorySlotRecord, ItemBarSlotRecord, SpaceTimeClient } from '../spacetime/SpaceTimeClient';
import type { SkillData } from '../spacetime/modules/SkillModule';
import type { ItemCatalogEntry, EquipmentSlotRecord, StorageSlotRecord } from '../spacetime/types';
import { t } from '../i18n';
import {
    localizeItemName,
} from '../i18n/content';
import { ActionTooltip } from './ActionTooltip';
import { createItemTooltipContent, mergeItemMetadata } from './itemTooltip';
import { ItemGrid } from './itemGrid/ItemGrid';
import type { ItemSlot } from './itemGrid/types';
import { renderSkillsList } from './renderSkillsList';
import { windowRegistry } from './windowing';
import type { WindowHandle } from './windowing/WindowRegistry';
import {
    getEquipDividerWidth,
    saveEquipDividerWidth,
    getSkillsScrollTop,
    saveSkillsScrollTop,
} from '../settings/uiState';
import { windowId } from './windowing';
import { buildSkillBarSignature, buildSkillListSignature } from './skillListStability';

type CharacterTab = 'items' | 'skills' | 'equipment' | 'storage';
type TooltipItem = Pick<InventorySlotRecord, 'itemId' | 'quantity' | 'tradeable' | 'metadata'>;
type InventoryLikeSlot = Pick<InventorySlotRecord, 'slotIndex' | 'inventoryId' | 'itemId' | 'quantity' | 'metadata'>;
type StorageLikeSlot = Pick<StorageSlotRecord, 'slotIndex' | 'storageId' | 'itemId' | 'quantity' | 'metadata'>;

export class PlayerInventory {
    private readonly client: SpaceTimeClient;
    private readonly container: HTMLElement;
    private readonly contentArea: HTMLElement;
    private readonly closeBtn: HTMLButtonElement;

    private items: InventorySlotRecord[] = [];
    private itemBarItems: ItemBarSlotRecord[] = [];
    private itemCatalogById = new Map<string, ItemCatalogEntry>();
    private equipmentSlots: EquipmentSlotRecord[] = [];
    private storageSlots: StorageSlotRecord[] = [];
    private skills: SkillData[] = [];
    private skillBarSkillIds: string[] = [];
    private skillsSignature = '';
    private skillBarSkillIdsSignature = '';
    private isStorageAvailable = false;
    private storageCapacity = 100;
    private capacity: number = 10;
    private activeTab: CharacterTab = 'items';
    private defaultPosition = { x: 0, y: 0 };
    private readonly defaultSize = { width: 520, height: 420 };
    private readonly MIN_WIDTH = 320;
    private readonly MIN_HEIGHT = 280;
    private readonly windowHandle: WindowHandle;
    private readonly onOpenCallbacks: Array<() => void> = [];
    private readonly onCloseCallbacks: Array<() => void> = [];
    private readonly tooltip = new ActionTooltip();
    private itemsGrid?: ItemGrid;
    private equipInvGrid?: ItemGrid;
    private storageInvGrid?: ItemGrid;
    private storageGrid?: ItemGrid;
    private storageLayout?: HTMLElement;

    // Persistent Tab Roots
    private itemsRoot?: HTMLElement;
    private skillsRoot?: HTMLElement;
    private skillsListScrollRoot?: HTMLElement;
    private equipRoot?: HTMLElement;
    private storageRoot?: HTMLElement;
    private dividerMoveListener: ((e: MouseEvent) => void) | null = null;
    private dividerUpListener: (() => void) | null = null;
    private contextMenuEl: HTMLDivElement | null = null;
    private contextMenuCloseHandler: ((event: MouseEvent) => void) | null = null;
    private readonly onSkillsScroll = (): void => {
        if (!this.skillsListScrollRoot) return;
        saveSkillsScrollTop(this.skillsListScrollRoot.scrollTop);
    };
    private readonly searchQueries = new Map<CharacterTab, string>();

    // Tab bar elements
    private tabBar: HTMLElement;
    private tabButtons: Map<CharacterTab, HTMLButtonElement> = new Map();

    constructor(
        client: SpaceTimeClient,
        container: HTMLElement,
        _grid: HTMLElement,
        closeBtn: HTMLButtonElement
    ) {
        this.client = client;
        this.container = container;
        const header = this.container.querySelector<HTMLElement>('.window-header');
        const resizeHandle = this.container.querySelector<HTMLElement>('.window-resize-handle');
        const contentArea = this.container.querySelector<HTMLElement>('.window-content');
        if (!header || !resizeHandle || !contentArea) {
            throw new Error('Inventory window is missing drag/resize elements');
        }
        this.contentArea = contentArea;
        this.closeBtn = closeBtn;
        this.centerWindow();
        this.windowHandle = windowRegistry.register({
            id: windowId('inventory', 'main'),
            container: this.container as HTMLDivElement,
            header: header as HTMLDivElement,
            resizeHandle: resizeHandle as HTMLDivElement,
            closeButton: this.closeBtn,
            minWidth: this.MIN_WIDTH,
            minHeight: this.MIN_HEIGHT,
            defaultPosition: this.defaultPosition,
            defaultSize: this.defaultSize,
            defaultTab: 'items',
            allowedTabs: ['items', 'skills', 'equipment', 'storage'],
        });
        this.activeTab = this.windowHandle.getActiveTab() as CharacterTab;

        // Build tab bar
        this.tabBar = this.buildTabBar();
        // Insert tab bar after header, before content
        header.after(this.tabBar);

        this.closeBtn.addEventListener('click', () => this.hide());
        if (this.windowHandle.getWindowState() === 'open') {
            this.show();
        } else {
            this.hide();
        }
    }

    public updateItems(items: InventorySlotRecord[]): void {
        this.items = items;
        if (this.activeTab === 'items' || this.activeTab === 'equipment' || this.activeTab === 'storage') this.render();
    }

    public updateItemBar(items: ItemBarSlotRecord[]): void {
        this.itemBarItems = items;
        if (this.activeTab === 'items') this.render();
    }

    public updateItemCatalog(entries: ItemCatalogEntry[]): void {
        this.itemCatalogById = new Map(entries.map((entry) => [entry.itemId, entry]));
        if (this.activeTab === 'items' || this.activeTab === 'equipment' || this.activeTab === 'storage') this.render();
    }

    public updateEquipment(slots: EquipmentSlotRecord[]): void {
        this.equipmentSlots = slots;
        if (this.activeTab === 'equipment') this.render();
    }

    public updateStorage(slots: StorageSlotRecord[], capacity?: number): void {
        this.storageSlots = slots;
        if (typeof capacity === 'number' && Number.isFinite(capacity) && capacity > 0) {
            this.storageCapacity = Math.floor(capacity);
        }
        if (this.activeTab === 'storage') this.render();
    }

    public setStorageAvailable(isAvailable: boolean): void {
        this.isStorageAvailable = isAvailable;
        if (!isAvailable) {
            this.storageSlots = [];
        }
        if (!isAvailable && this.activeTab === 'storage') {
            this.activeTab = 'items';
        }
        this.updateTabStyles();
        this.render();
    }

    public updateStorageCapacity(capacity: number): void {
        if (!Number.isFinite(capacity) || capacity <= 0) return;
        const next = Math.floor(capacity);
        if (next === this.storageCapacity) return;
        this.storageCapacity = next;
        if (this.activeTab === 'storage') this.render();
    }

    public updateCapacity(capacity: number): void {
        if (this.capacity !== capacity && capacity > 0) {
            this.capacity = capacity;
            if (this.activeTab === 'items') this.render();
        } else if (this.capacity !== capacity) {
            this.capacity = capacity;
        }
    }

    public updateSkills(skills: SkillData[]): void {
        const nextSignature = buildSkillListSignature(skills);
        if (nextSignature === this.skillsSignature) {
            return;
        }
        this.skills = skills;
        this.skillsSignature = nextSignature;
        if (this.activeTab === 'skills') this.render();
    }

    public updateSkillBar(skillIds: string[]): void {
        const nextSignature = buildSkillBarSignature(skillIds);
        if (nextSignature === this.skillBarSkillIdsSignature) {
            return;
        }
        this.skillBarSkillIds = skillIds;
        this.skillBarSkillIdsSignature = nextSignature;
        if (this.activeTab === 'skills') this.render();
    }

    public toggleVisibility(): void {
        if (this.container.hidden) {
            this.show();
        } else {
            this.hide();
        }
    }

    public openTab(tab: CharacterTab): void {
        this.setActiveTab(tab);
        this.show();
    }

    public isOpenOnTab(tab: CharacterTab): boolean {
        return this.isVisible && this.activeTab === tab;
    }

    public show(): void {
        if (!this.container.hidden) return;
        this.container.hidden = false;
        this.windowHandle.setWindowState('open');
        this.onOpenCallbacks.forEach((callback) => callback());
        this.render();
    }

    public hide(): void {
        if (this.container.hidden) return;
        this.tooltip.hide();
        this.hideContextMenu();
        this.container.hidden = true;
        this.windowHandle.setWindowState('closed');
        this.onCloseCallbacks.forEach((callback) => callback());
    }

    public get isVisible(): boolean {
        return !this.container.hidden;
    }

    public onOpen(callback: () => void): void {
        this.onOpenCallbacks.push(callback);
    }

    public onClose(callback: () => void): void {
        this.onCloseCallbacks.push(callback);
    }

    private buildTabBar(): HTMLElement {
        const bar = document.createElement('div');
        bar.className = 'char-tab-bar';

        const tabs: { key: CharacterTab; label: string }[] = [
            { key: 'items', label: t('character.tabs.items') },
            { key: 'skills', label: t('character.tabs.skills') },
            { key: 'equipment', label: t('character.tabs.equipment') },
            { key: 'storage', label: t('character.tabs.storage') },
        ];

        for (const { key, label } of tabs) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.textContent = label;
            btn.className = 'char-tab-btn';
            btn.addEventListener('click', () => this.setActiveTab(key));
            bar.appendChild(btn);
            this.tabButtons.set(key, btn);
        }

        this.updateTabStyles();
        return bar;
    }

    private setActiveTab(tab: CharacterTab): void {
        this.activeTab = tab;
        this.windowHandle.setActiveTab(tab);
        this.updateTabStyles();
        this.render();
    }

    private updateTabStyles(): void {
        for (const [key, btn] of this.tabButtons) {
            if (key === 'storage') {
                btn.style.display = this.isStorageAvailable ? '' : 'none';
            }
            if (key === this.activeTab) {
                btn.classList.add('active');
            } else {
                btn.classList.remove('active');
            }
        }
    }

    private render(): void {
        if (!this.isVisible) return;

        const activeElement = document.activeElement;
        const shouldRestoreSearchFocus =
            activeElement instanceof HTMLInputElement
            && activeElement.classList.contains('inventory-search-input')
            && this.contentArea.contains(activeElement);
        const selectionStart = shouldRestoreSearchFocus ? activeElement.selectionStart : null;
        const selectionEnd = shouldRestoreSearchFocus ? activeElement.selectionEnd : null;

        this.tooltip.hide();

        // Ensure all persistent roots are created and attached
        if (!this.itemsRoot) {
            this.itemsRoot = document.createElement('div');
            this.itemsRoot.className = 'inventory-tab-content';
            this.contentArea.appendChild(this.itemsRoot);
        }
        if (!this.skillsRoot) {
            this.skillsRoot = document.createElement('div');
            this.skillsRoot.className = 'inventory-tab-content';
            this.skillsRoot.addEventListener('scroll', this.onSkillsScroll);
            this.contentArea.appendChild(this.skillsRoot);
        }
        if (!this.equipRoot) {
            this.equipRoot = document.createElement('div');
            this.equipRoot.className = 'inventory-tab-content';
            this.contentArea.appendChild(this.equipRoot);
        }
        if (!this.storageRoot) {
            this.storageRoot = document.createElement('div');
            this.storageRoot.className = 'inventory-tab-content';
            this.contentArea.appendChild(this.storageRoot);
        }

        // Toggle visibility based on active tab
        this.contentArea.style.overflowY = this.activeTab === 'skills' ? 'hidden' : 'auto';
        this.itemsRoot.style.display = this.activeTab === 'items' ? 'block' : 'none';
        this.skillsRoot.style.display = this.activeTab === 'skills' ? 'block' : 'none';
        this.equipRoot.style.display = this.activeTab === 'equipment' ? 'block' : 'none';
        this.storageRoot.style.display = this.activeTab === 'storage' ? 'block' : 'none';

        switch (this.activeTab) {
            case 'items':
                this.renderItems();
                break;
            case 'skills':
                this.renderSkills();
                break;
            case 'equipment':
                this.renderEquipment();
                break;
            case 'storage':
                this.renderStorage();
                break;
        }

        if (shouldRestoreSearchFocus) {
            const activeRoot = this.getTabRoot(this.activeTab);
            const nextInput = activeRoot?.querySelector<HTMLInputElement>('.inventory-search-input') ?? null;
            if (nextInput) {
                nextInput.focus();
                if (selectionStart !== null && selectionEnd !== null) {
                    nextInput.setSelectionRange(selectionStart, selectionEnd);
                }
            }
        }
    }

    private renderItems(): void {
        if (!this.itemsRoot) return;

        this.itemsRoot.innerHTML = '';
        this.itemsRoot.appendChild(this.createTabSearchInput('items', 'Search items'));

        const filteredItems = this.items.filter((slotData) =>
            this.matchesItemSearch(slotData.itemId, slotData.metadata, this.getNormalizedSearchQuery('items'))
        );

        const slots: ItemSlot[] = filteredItems.map((slotData) =>
            this.createInventoryItemSlot(slotData, {
                className: `inventory-slot ${this.isItemInBar(slotData.inventoryId) ? 'in-bar' : ''}`,
            })
        );

        const capacity = Math.max(this.capacity, slots.length);

        if (!this.itemsGrid) {
            this.itemsGrid = new ItemGrid({
                gridId: 'player-inventory-items',
                label: t('character.tabs.items'),
                capacity,
                slots,
            });
            this.bindGridTooltipHandlers(this.itemsGrid, (slotId) =>
                filteredItems.find((i) => i.slotIndex === Number(slotId))
            );
            this.itemsGrid.onSlotClick = (slotId) => {
                const slotData = filteredItems.find((i) => i.slotIndex === Number(slotId));
                if (slotData) this.handleItemClick(slotData);
            };
            this.itemsGrid.onSlotContextMenu = (slotId, event) => {
                const slotData = filteredItems.find((i) => i.slotIndex === Number(slotId));
                if (!slotData || slotData.quantity <= 1) return;
                this.showSplitContextMenu(slotData, event.clientX, event.clientY);
            };
            this.itemsGrid.onDrop = (targetSlotId, data) => {
                if (data.gridId !== 'player-inventory-items') return;
                const source = filteredItems.find(i => i.slotIndex === Number(data.slotId));
                const targetSlotIndex = Number(targetSlotId);
                if (!source || !Number.isFinite(targetSlotIndex) || source.slotIndex === targetSlotIndex) return;
                this.client.inventory.moveInventoryItem(source.inventoryId, targetSlotIndex)
                    .catch((err) => console.error('[Inventory] Failed to move item', err));
            };
        } else {
            this.itemsGrid.update(slots, capacity);
        }
        this.itemsRoot.appendChild(this.itemsGrid.element);
    }

    private renderSkills(): void {
        if (!this.skillsRoot) return;

        const previousScrollTop = this.skillsListScrollRoot?.scrollTop ?? 0;
        this.skillsRoot.innerHTML = '';
        this.skillsRoot.style.display = 'flex';
        this.skillsRoot.style.flexDirection = 'column';
        this.skillsRoot.style.height = '100%';
        this.skillsRoot.style.minHeight = '0';
        this.skillsRoot.style.overflow = 'hidden';
        this.skillsRoot.appendChild(this.createTabSearchInput('skills', 'Search skills'));

        const listHost = document.createElement('div');
        listHost.style.flex = '1';
        listHost.style.minHeight = '0';
        listHost.style.overflowY = 'auto';
        listHost.style.paddingRight = '4px';
        listHost.addEventListener('scroll', this.onSkillsScroll);
        this.skillsListScrollRoot?.removeEventListener('scroll', this.onSkillsScroll);
        this.skillsListScrollRoot = listHost;
        this.skillsRoot.appendChild(listHost);
        const query = this.getNormalizedSearchQuery('skills');
        const filteredSkills = this.skills.filter((skill) => {
            if (!query) return true;
            const haystack = `${skill.skillId} ${skill.displayName ?? ''} ${skill.description ?? ''}`.toLowerCase();
            return haystack.includes(query);
        });

        renderSkillsList(listHost, filteredSkills, this.skillBarSkillIds, {
            enableInteraction: true,
            onSkillClick: (skillId) => this.handleSkillClick(skillId),
        });
        this.skillsListScrollRoot.scrollTop = previousScrollTop || getSkillsScrollTop();
    }

    private renderEquipment(): void {
        if (!this.equipRoot) return;

        // Remove stale divider listeners before rebuilding
        if (this.dividerMoveListener) window.removeEventListener('mousemove', this.dividerMoveListener);
        if (this.dividerUpListener) window.removeEventListener('mouseup', this.dividerUpListener);
        this.dividerMoveListener = null;
        this.dividerUpListener = null;

        this.equipRoot.innerHTML = '';
        this.equipRoot.appendChild(this.createTabSearchInput('equipment', 'Search equipment'));

        const root = document.createElement('div');
        root.className = 'equip-tab-root';

        const body = document.createElement('div');
        body.className = 'equip-body';
        body.style.width = `${getEquipDividerWidth(160)}px`;
        body.style.minWidth = '0';
        body.style.flexShrink = '0';

        const slotMap = new Map(this.equipmentSlots.map((s) => [s.slotName, s]));

        const makeSlot = (slotName: string, label: string, mirrored = false): HTMLElement => {
            const wrap = document.createElement('div');
            wrap.className = 'equip-slot-wrap';

            const slot = document.createElement('div');
            slot.className = 'equip-body-slot';
            slot.dataset['slotName'] = slotName;
            slot.title = label;

            const equipped = slotMap.get(slotName);
            if (equipped) {
                const catalogEntry = this.itemCatalogById.get(equipped.itemId);
                const mergedMeta = mergeItemMetadata(equipped.metadata, catalogEntry?.metadata);
                const emoji = typeof mergedMeta.emoji === 'string' ? mergedMeta.emoji : '📦';
                const emojiEl = document.createElement('div');
                emojiEl.className = 'emoji';
                emojiEl.textContent = emoji;
                if (mirrored) emojiEl.style.transform = 'scaleX(-1)';
                slot.appendChild(emojiEl);

                if (equipped.inventoryId) {
                    slot.addEventListener('contextmenu', (e) => {
                        e.preventDefault();
                        this.client.inventory.unequipItem(slotName)
                            .catch((err) => console.error('[Equipment] Failed to unequip', err));
                    });

                    slot.draggable = true;
                    slot.addEventListener('dragstart', (e) => {
                        e.dataTransfer!.setData('application/inventory-id', equipped.inventoryId);
                        e.dataTransfer!.setData('application/equip-source-slot', slotName);
                        e.dataTransfer!.effectAllowed = 'move';
                        slot.classList.add('dragging');
                    });
                    slot.addEventListener('dragend', () => slot.classList.remove('dragging'));
                }
            } else {
                const labelEl = document.createElement('div');
                labelEl.className = 'equip-slot-label';
                labelEl.textContent = label;
                slot.appendChild(labelEl);
            }

            slot.addEventListener('dragover', (e) => {
                if (e.dataTransfer?.types.includes('application/inventory-id')) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    slot.classList.add('drag-over');
                }
            });
                slot.addEventListener('dragleave', () => slot.classList.remove('drag-over'));
                slot.addEventListener('drop', (e) => {
                    e.preventDefault();
                    slot.classList.remove('drag-over');
                    const inventoryId = e.dataTransfer?.getData('application/inventory-id')
                        ?? e.dataTransfer?.getData('application/item-grid-slot');
                    const sourceSlot = e.dataTransfer?.getData('application/equip-source-slot');
                    if (!inventoryId) return;
                if (sourceSlot) {
                    this.client.inventory.unequipItem(sourceSlot)
                        .then(() => this.client.inventory.equipItem(slotName, inventoryId))
                        .catch((err) => console.error('[Equipment] Failed to move between slots', err));
                } else {
                    this.client.inventory.equipItem(slotName, inventoryId)
                        .catch((err) => console.error('[Equipment] Failed to equip', err));
                }
            });

            wrap.appendChild(slot);
            return wrap;
        };

        const makeMirroredPair = (slotName: string, leftLabel: string, rightLabel: string): [HTMLElement, HTMLElement] => {
            return [makeSlot(slotName, leftLabel, false), makeSlot(slotName, rightLabel, true)];
        };

        const [leftEar, rightEar] = makeMirroredPair('earrings', 'L Ear', 'R Ear');
        const head = makeSlot('head', 'Head');
        const necklace = makeSlot('necklace', 'Neck');
        const [leftSho, rightSho] = makeMirroredPair('shoulders', 'L Shoulder', 'R Shoulder');
        const chest = makeSlot('chest', 'Chest');
        const leftRing = makeSlot('left_ring', 'L Ring');
        const rightRing = makeSlot('right_ring', 'R Ring');
        const legs = makeSlot('legs', 'Legs');
        const [leftGlove, rightGlove] = makeMirroredPair('gloves', 'L Glove', 'R Glove');
        const [leftBoot, rightBoot] = makeMirroredPair('boots', 'L Boot', 'R Boot');

        // Row 1: L ear | head | R ear
        const r1 = document.createElement('div'); r1.className = 'equip-row';
        r1.append(leftEar, head, rightEar);

        // Row 2: L shoulder | neck | R shoulder
        const r2 = document.createElement('div'); r2.className = 'equip-row';
        r2.append(leftSho, necklace, rightSho);

        // Row 3: L glove | chest | R glove
        const r3 = document.createElement('div'); r3.className = 'equip-row';
        r3.append(leftGlove, chest, rightGlove);

        // Row 4: L ring | legs | R ring
        const r4 = document.createElement('div'); r4.className = 'equip-row';
        r4.append(leftRing, legs, rightRing);

        // Row 5: L boot | (gap) | R boot
        const r5 = document.createElement('div'); r5.className = 'equip-row';
        const gap5 = document.createElement('div'); gap5.className = 'equip-gap';
        r5.append(leftBoot, gap5, rightBoot);

        body.append(r1, r2, r3, r4, r5);

        const inv = document.createElement('div');
        inv.className = 'equip-inv';

        const equipmentQuery = this.getNormalizedSearchQuery('equipment');
        const equipItems = this.items.filter((item) => {
            const catalogEntry = this.itemCatalogById.get(item.itemId);
            return catalogEntry?.itemType === 'equip'
                && this.matchesItemSearch(item.itemId, item.metadata, equipmentQuery);
        });

        if (equipItems.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'char-tab-empty';
            empty.textContent = t('character.noEquipment');
            inv.appendChild(empty);
            if (this.equipInvGrid) {
                this.equipInvGrid.destroy();
                this.equipInvGrid = undefined;
            }
        } else {
            const slots: ItemSlot[] = equipItems.map(item => {
                const catalogEntry = this.itemCatalogById.get(item.itemId);
                const mergedMeta = mergeItemMetadata(item.metadata, catalogEntry?.metadata);
                const emoji = typeof mergedMeta.emoji === 'string' ? mergedMeta.emoji : '📦';
                const displayName = localizeItemName(item.itemId, mergedMeta, catalogEntry?.displayName ?? item.itemId);

                return {
                    id: item.inventoryId,
                    className: 'inventory-slot',
                    item: {
                        emoji,
                        displayName,
                        quantity: 1,
                    }
                };
            });

            if (!this.equipInvGrid) {
                this.equipInvGrid = new ItemGrid({
                    gridId: 'player-inventory-equipment',
                    label: t('character.tabs.items'),
                    capacity: slots.length,
                    slots,
                });
                this.equipInvGrid.onSlotMouseEnter = (slotId, event) => {
                    const item = equipItems.find(i => i.inventoryId === slotId);
                    if (item) this.showItemTooltip(item, event);
                };
                this.equipInvGrid.onSlotMouseMove = (_, event) => {
                    this.tooltip.move(event.clientX, event.clientY);
                };
                this.equipInvGrid.onSlotMouseLeave = () => {
                    this.tooltip.hide();
                };
                // For drag and drop back to equip slots, we'll need to handle it 
                // but ItemGrid handles its own dragstart.
                // We don't have a GridDragDrop here yet because equip slots aren't ItemGrids.
                inv.appendChild(this.equipInvGrid.element);
            } else {
                this.equipInvGrid.update(slots, slots.length);
                inv.appendChild(this.equipInvGrid.element);
            }
        }

        // Draggable divider between body silhouette and inventory
        const divider = document.createElement('div');
        divider.className = 'equip-divider';

        let dividerDragging = false;
        let dividerStartX = 0;
        let bodyStartWidth = 0;

        divider.addEventListener('mousedown', (e) => {
            dividerDragging = true;
            dividerStartX = e.clientX;
            bodyStartWidth = body.getBoundingClientRect().width;
            e.preventDefault();
        });

        this.dividerMoveListener = (e: MouseEvent): void => {
            if (!dividerDragging) return;
            const dx = e.clientX - dividerStartX;
            const newWidth = Math.max(160, Math.min(400, bodyStartWidth + dx));
            body.style.width = `${newWidth}px`;
            body.style.minWidth = '0';
            body.style.flexShrink = '0';
        };

        this.dividerUpListener = (): void => {
            if (dividerDragging) {
                saveEquipDividerWidth(body.getBoundingClientRect().width);
            }
            dividerDragging = false;
        };

        window.addEventListener('mousemove', this.dividerMoveListener);
        window.addEventListener('mouseup', this.dividerUpListener);

        root.append(body, divider, inv);
        this.equipRoot.appendChild(root);
    }

    private renderStorage(): void {
        if (!this.storageRoot) return;
        this.storageRoot.innerHTML = '';
        this.storageRoot.appendChild(this.createTabSearchInput('storage', 'Search storage'));
        if (!this.storageLayout) {
            this.storageLayout = document.createElement('div');
            this.storageLayout.style.display = 'grid';
            this.storageLayout.style.gridTemplateColumns = '1fr 1fr';
            this.storageLayout.style.gap = '4px';
            this.storageLayout.style.alignItems = 'start';
            this.storageLayout.style.minHeight = '0';
        }
        this.storageRoot.appendChild(this.storageLayout);

        const storageQuery = this.getNormalizedSearchQuery('storage');
        const filteredInventoryItems = this.items.filter((slotData) =>
            this.matchesItemSearch(slotData.itemId, slotData.metadata, storageQuery)
        );
        const filteredStorageItems = this.storageSlots.filter((slotData) =>
            this.matchesItemSearch(slotData.itemId, slotData.metadata, storageQuery)
        );
        const inventorySlots: ItemSlot[] = filteredInventoryItems.map((slotData) => this.createInventoryItemSlot(slotData));
        const storageSlots: ItemSlot[] = filteredStorageItems.map((slotData) => this.createStorageItemSlot(slotData));

        if (!this.storageInvGrid) {
            this.storageInvGrid = new ItemGrid({
                gridId: 'player-inventory-storage-source',
                label: t('character.tabs.items'),
                capacity: Math.max(this.capacity, inventorySlots.length),
                slots: inventorySlots,
            });
            this.bindGridTooltipHandlers(this.storageInvGrid, (slotId) =>
                filteredInventoryItems.find((i) => i.slotIndex === Number(slotId))
            );
            this.storageInvGrid.onSlotContextMenu = (slotId, event) => {
                const slotData = filteredInventoryItems.find((i) => i.slotIndex === Number(slotId));
                if (!slotData || slotData.quantity <= 1) return;
                this.showSplitContextMenu(slotData, event.clientX, event.clientY);
            };
            this.storageInvGrid.onDrop = (targetSlotId, data) => {
                if (data.gridId === 'player-storage-items') {
                    const source = filteredStorageItems.find(i => i.slotIndex === Number(data.slotId));
                    const targetSlotIndex = Number(targetSlotId);
                    if (!source || !Number.isFinite(targetSlotIndex)) return;
                    this.client.inventory.moveStorageToInventory(source.storageId, targetSlotIndex)
                        .catch((err) => console.error('[Storage] Failed to move storage item to inventory', err));
                } else if (data.gridId === 'player-inventory-storage-source') {
                    const source = filteredInventoryItems.find(i => i.slotIndex === Number(data.slotId));
                    const targetSlotIndex = Number(targetSlotId);
                    if (!source || !Number.isFinite(targetSlotIndex) || source.slotIndex === targetSlotIndex) return;
                    this.client.inventory.moveInventoryItem(source.inventoryId, targetSlotIndex)
                        .catch((err) => console.error('[Storage] Failed to move inventory item', err));
                }
            };
        } else {
            this.storageInvGrid.update(inventorySlots, Math.max(this.capacity, inventorySlots.length));
        }
        this.storageLayout.appendChild(this.storageInvGrid.element);
        if (!this.storageGrid) {
            this.storageGrid = new ItemGrid({
                gridId: 'player-storage-items',
                label: t('character.tabs.storage'),
                capacity: this.storageCapacity,
                slots: storageSlots,
            });
            this.bindGridTooltipHandlers(this.storageGrid, (slotId) =>
                filteredStorageItems.find((i) => i.slotIndex === Number(slotId))
            );
            this.storageGrid.onDrop = (targetSlotId, data) => {
                const targetSlotIndex = Number(targetSlotId);
                if (!Number.isFinite(targetSlotIndex)) return;
                if (data.gridId === 'player-inventory-storage-source') {
                    const source = filteredInventoryItems.find(i => i.slotIndex === Number(data.slotId));
                    if (!source) return;
                    this.client.inventory.moveInventoryToStorage(source.inventoryId, targetSlotIndex)
                        .catch((err) => console.error('[Storage] Failed to move inventory item to storage', err));
                } else if (data.gridId === 'player-storage-items') {
                    const source = filteredStorageItems.find(i => i.slotIndex === Number(data.slotId));
                    if (!source || source.slotIndex === targetSlotIndex) return;
                    this.client.inventory.moveStorageItem(source.storageId, targetSlotIndex)
                        .catch((err) => console.error('[Storage] Failed to move storage item', err));
                }
            };
        } else {
            this.storageGrid.update(storageSlots, this.storageCapacity);
        }
        this.storageLayout.appendChild(this.storageGrid.element);
    }

    private isItemInBar(inventoryId: string): boolean {
        return this.itemBarItems.some((item) => item.inventoryId === inventoryId);
    }

    private createInventoryItemSlot(
        slotData: InventoryLikeSlot,
        options: { className?: string } = {}
    ): ItemSlot {
        return this.createCommonItemSlot({
            slotIndex: slotData.slotIndex,
            dragDataId: slotData.inventoryId,
            itemId: slotData.itemId,
            quantity: slotData.quantity,
            metadata: slotData.metadata,
            className: options.className ?? 'inventory-slot',
        });
    }

    private createStorageItemSlot(slotData: StorageLikeSlot): ItemSlot {
        return this.createCommonItemSlot({
            slotIndex: slotData.slotIndex,
            dragDataId: slotData.storageId,
            itemId: slotData.itemId,
            quantity: slotData.quantity,
            metadata: slotData.metadata,
            className: 'inventory-slot',
        });
    }

    private createCommonItemSlot(params: {
        slotIndex: number;
        dragDataId: string;
        itemId: string;
        quantity: number;
        metadata: Record<string, unknown>;
        className: string;
    }): ItemSlot {
        const catalogEntry = this.itemCatalogById.get(params.itemId);
        const mergedMetadata = mergeItemMetadata(params.metadata, catalogEntry?.metadata);
        const displayName = localizeItemName(params.itemId, mergedMetadata, catalogEntry?.displayName ?? params.itemId);
        const emoji = this.resolveItemEmoji(params.metadata, mergedMetadata);

        return {
            id: String(params.slotIndex),
            dragDataId: params.dragDataId,
            className: params.className,
            item: {
                emoji,
                displayName,
                quantity: params.quantity,
            },
        };
    }

    private createTabSearchInput(tab: CharacterTab, placeholder: string): HTMLInputElement {
        const input = document.createElement('input');
        input.type = 'search';
        input.className = 'inventory-search-input';
        input.placeholder = placeholder;
        input.value = this.searchQueries.get(tab) ?? '';
        Object.assign(input.style, {
            width: '100%',
            boxSizing: 'border-box',
            marginBottom: '10px',
            padding: '8px 10px',
            borderRadius: '6px',
            border: '1px solid rgba(148, 163, 184, 0.3)',
            background: 'rgba(15, 23, 42, 0.75)',
            color: '#f8fafc',
        });
        input.addEventListener('input', () => {
            this.searchQueries.set(tab, input.value);
            this.render();
        });
        input.addEventListener('keydown', (event) => event.stopPropagation());
        return input;
    }

    private getTabRoot(tab: CharacterTab): HTMLElement | undefined {
        switch (tab) {
            case 'items':
                return this.itemsRoot;
            case 'skills':
                return this.skillsRoot;
            case 'equipment':
                return this.equipRoot;
            case 'storage':
                return this.storageRoot;
        }
    }

    private getNormalizedSearchQuery(tab: CharacterTab): string {
        return (this.searchQueries.get(tab) ?? '').trim().toLowerCase();
    }

    private matchesItemSearch(itemId: string, metadata: Record<string, unknown>, query: string): boolean {
        if (!query) return true;
        const catalogEntry = this.itemCatalogById.get(itemId);
        const mergedMetadata = mergeItemMetadata(metadata, catalogEntry?.metadata);
        const displayName = localizeItemName(itemId, mergedMetadata, catalogEntry?.displayName ?? itemId);
        return `${itemId} ${displayName}`.toLowerCase().includes(query);
    }

    private bindGridTooltipHandlers(
        grid: ItemGrid,
        resolveSlotData: (slotId: string) => TooltipItem | undefined
    ): void {
        grid.onSlotMouseEnter = (slotId, event) => {
            const slotData = resolveSlotData(slotId);
            if (slotData) this.showItemTooltip(slotData, event);
        };
        grid.onSlotMouseMove = (_, event) => this.tooltip.move(event.clientX, event.clientY);
        grid.onSlotMouseLeave = () => this.tooltip.hide();
    }

    private handleItemClick(slotData: InventorySlotRecord): void {
        const isInBar = this.itemBarItems.some(item => item.inventoryId === slotData.inventoryId);
        if (isInBar) return;

        this.client.inventory.addToItemBar(slotData.inventoryId)
            .catch(err => console.error('[Inventory] Failed to add to bar', err));
    }

    private handleSkillClick(skillId: string): void {
        if (this.skillBarSkillIds.includes(skillId)) return;

        let firstEmptySlot = -1;
        for (let i = 0; i < 9; i += 1) {
            const slotSkillId = this.skillBarSkillIds[i];
            if (!slotSkillId) {
                firstEmptySlot = i;
                break;
            }
        }
        if (firstEmptySlot < 0) return;

        this.client.skills.addToSkillBarAt(skillId, firstEmptySlot)
            .catch((err) => console.error('[Inventory] Failed to add skill to bar', err));
    }

    private showItemTooltip(slotData: TooltipItem, event: MouseEvent): void {
        const catalogMetadata = this.itemCatalogById.get(slotData.itemId)?.metadata;
        this.tooltip.show(
            createItemTooltipContent(slotData.itemId, slotData.quantity, slotData.metadata, { catalogMetadata, tradeable: slotData.tradeable }),
            event.clientX,
            event.clientY
        );
    }

    private resolveItemEmoji(
        itemMetadata: Record<string, unknown>,
        mergedMetadata: Record<string, unknown>
    ): string {
        const itemEmoji = typeof itemMetadata.emoji === 'string' ? itemMetadata.emoji : undefined;
        const mergedEmoji = typeof mergedMetadata.emoji === 'string' ? mergedMetadata.emoji : undefined;
        return itemEmoji ?? mergedEmoji ?? '📦';
    }

    private showSplitContextMenu(slotData: InventorySlotRecord, x: number, y: number): void {
        this.hideContextMenu();

        const menu = document.createElement('div');
        Object.assign(menu.style, {
            position: 'fixed',
            left: `${x}px`,
            top: `${y}px`,
            background: '#0f172a',
            border: '1px solid #334155',
            borderRadius: '6px',
            padding: '4px',
            zIndex: '100000',
            minWidth: '140px',
            boxShadow: '0 8px 18px rgba(0,0,0,0.45)',
        });

        const splitBtn = document.createElement('button');
        splitBtn.type = 'button';
        splitBtn.textContent = 'Split item';
        Object.assign(splitBtn.style, {
            width: '100%',
            textAlign: 'left',
            background: 'transparent',
            border: 'none',
            color: '#e2e8f0',
            padding: '6px 8px',
            cursor: 'pointer',
            borderRadius: '4px',
        });
        splitBtn.addEventListener('mouseenter', () => {
            splitBtn.style.background = 'rgba(148, 163, 184, 0.16)';
        });
        splitBtn.addEventListener('mouseleave', () => {
            splitBtn.style.background = 'transparent';
        });
        splitBtn.addEventListener('click', () => {
            this.hideContextMenu();
            const max = slotData.quantity - 1;
            const raw = window.prompt(`Split amount (1-${max})`, '1');
            if (raw === null) return;
            const quantity = Number(raw);
            if (!Number.isInteger(quantity) || quantity < 1 || quantity > max) return;
            this.client.inventory.splitInventoryItem(slotData.inventoryId, quantity)
                .catch((err) => console.error('[Inventory] Failed to split item', err));
        });

        menu.appendChild(splitBtn);
        document.body.appendChild(menu);
        this.contextMenuEl = menu;
        this.contextMenuCloseHandler = (event: MouseEvent) => {
            if (!menu.contains(event.target as Node)) {
                this.hideContextMenu();
            }
        };
        window.setTimeout(() => {
            if (this.contextMenuCloseHandler) {
                window.addEventListener('mousedown', this.contextMenuCloseHandler);
            }
        }, 0);
    }

    private hideContextMenu(): void {
        if (this.contextMenuCloseHandler) {
            window.removeEventListener('mousedown', this.contextMenuCloseHandler);
            this.contextMenuCloseHandler = null;
        }
        if (this.contextMenuEl) {
            this.contextMenuEl.remove();
            this.contextMenuEl = null;
        }
    }

    private centerWindow(): void {
        this.defaultPosition = {
            x: Math.max(0, Math.round((window.innerWidth - this.defaultSize.width) / 2)),
            y: Math.max(0, Math.round((window.innerHeight - this.defaultSize.height) / 2)),
        };
    }

    public destroy(): void {
        this.hideContextMenu();
        if (this.skillsListScrollRoot) this.skillsListScrollRoot.removeEventListener('scroll', this.onSkillsScroll);
        if (this.dividerMoveListener) window.removeEventListener('mousemove', this.dividerMoveListener);
        if (this.dividerUpListener) window.removeEventListener('mouseup', this.dividerUpListener);
        this.itemsGrid?.destroy();
        this.equipInvGrid?.destroy();
        this.storageInvGrid?.destroy();
        this.storageGrid?.destroy();
        this.tooltip.hide();
        this.windowHandle.destroy();
    }
}
